<!-- eslint-disable @typescript-eslint/no-magic-numbers -->
<script lang="ts" setup>
    import WidgetInputRadio from '@/components/WidgetInputRadio.ce.vue';
    import { type SurveyItem } from '@/typings/widget';

    const props = defineProps<{
        modelValue: string
        items: SurveyItem[]
    }>();

    // eslint-disable-next-line func-call-spacing
    const emit = defineEmits<{
        (event: 'update:modelValue', value: string): void
        (event: 'next'): void
    }>();

    const { t } = useI18n();

    const selectedModel: Ref<string> = ref(props.modelValue ?? '');

    const handleNextStep = async(): Promise<void> => {
        await nextTick(() => {
            emit('next');
        });
    };

    watch(() => selectedModel.value, (nVal: string) => {
        emit('update:modelValue', nVal);
    });
</script>

<template>
    <div class="survey-selection">
        <div class="survey-selection__content">
            <div class="survey-selection__intro">
                <span class="survey-selection__title">
                    {{ t('widget.quote.survey.title') }}
                </span>
                <span class="survey-selection__subtitle">
                    {{ t('widget.quote.survey.subtitle') }}
                </span>
            </div>
            <div class="survey-selection__list">
                <div class="survey-selection__list-content">
                    <template
                        v-for="item in items"
                        :key="item.value"
                    >
                        <WidgetInputRadio
                            v-model="selectedModel"
                            :input-value="item.value"
                            name="survey"
                            type="secondary"
                            @click="handleNextStep"
                        >
                            {{ item.notranslate ? item.label : t(item.label) }}
                        </WidgetInputRadio>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
    .survey-selection {
        display: flex;
        justify-content: center;
        block-size: 100%;
        inline-size: 100%;

        &__content {
            display: flex;
            flex-direction: column;
            gap: var(--size-7);
            inline-size: 100%;
            padding-block: var( --spacing-07);
            padding-inline: calc(var(--spacing-04) + var(--spacing-01));

            @include min-width(md) {
                padding-block-start: var(--spacing-09);
            }

            @include min-width(lg) {
                padding-block: var(--spacing-01);
            }
        }

        &__intro {
            display: flex;
            flex-direction: column;
            justify-content: center;

            @include min-width(md) {
                gap: var(--spacing-01);
            }

            @include min-width(lg) {
                gap: var(--spacing-02);
            }
        }

        &__title {
            color: var(--text-primary);
            font-family: var(--font-primary);
            font-size: var(--font-h4);
            font-weight: var(--font-weight-medium);
            line-height: var(--line-height-150);
            padding-inline: var(--spacing-05);
            text-align: center;
        }

        &__subtitle {
            text-align: center;
        }

        &__list {
            display: flex;
            align-items: center;
            justify-content: center;
        }

        &__list-content {
            display: flex;
            overflow: hidden;
            flex-direction: column;
            gap: var(--spacing-03);
            inset-block-start: 0;
            padding-block-start: var(--spacing-03);
            padding-inline: var(--spacing-02);

            @include min-width(md) {
                padding-block-start: var(--spacing-05);
                padding-inline: var(--spacing-07);
            }

            @include min-width(md) {
                padding-block-start: var(--spacing-03);
            }
        }
    }
</style>
